import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Route, Navigation, Loader2, AlertCircle, TrendingDown } from 'lucide-react';
import { optimizeTrip, MAX_GMAPS_STOPS } from '../lib/routeOptimizer';
import { openGoogleMapsMultiStop, haversineKm } from '../lib/navigateTo';
import { useDriverAuth, API } from '../context/DriverAuthContext';
import { orderPartyPrimary } from '../lib/orderParty';

/**
 * MultiStopRoute — summary banner for an entire delivery run.
 *
 * Renders ONLY when the driver has 2+ active orders with valid GPS
 * coords. Shows:
 *   - Total stops + total driving distance (OSRM) + estimated total ETA
 *   - The OPTIMAL delivery sequence (numbered list of customer names)
 *   - "Estimated savings" vs the naive order-of-receipt sequence
 *   - A single big "Navigate all stops" button → Google Maps with
 *     waypoints. Drivers tap once, hop in the van, and follow voice
 *     guidance for the whole run.
 *
 * Why the savings number matters: makes the optimizer's value tangible.
 * If we silently re-order stops, drivers may not realise they're saving
 * fuel — the "saves 4.2 km vs received order" badge proves the lift.
 *
 * @param {{
 *   orders: Array<{ id:string, customer_name:string, order_number?:string,
 *                   location_lat:number, location_lng:number }>,
 * }} props
 */
const MultiStopRoute = ({ orders }) => {
  const { authHeaders } = useDriverAuth();
  const [driverPos, setDriverPos] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [result, setResult] = useState(null); // { ordered, distance, duration, source }
  const [naiveDistance, setNaiveDistance] = useState(null); // meters (received order, sanity)
  const [loading, setLoading] = useState(false);

  // ----- Live driver position (one-shot; route doesn't need streaming) -----
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation not available');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoError(null);
        setDriverPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => setGeoError(err?.message || 'Location blocked'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  }, []);

  // ----- Optimize whenever orders or driver position changes -----
  // Key by (driverPos rounded to 100m) + order ids so we don't re-fetch
  // every render.
  const stops = orders
    .filter((o) => Number.isFinite(o.location_lat) && Number.isFinite(o.location_lng))
    .map((o) => ({
      id: o.id,
      lat: o.location_lat,
      lng: o.location_lng,
      label: orderPartyPrimary(o),
      order_number: o.order_number,
    }));
  const stopsKey = stops.map((s) => s.id).join(',');
  const driverKey = driverPos
    ? `${driverPos.lat.toFixed(3)},${driverPos.lng.toFixed(3)}`
    : null;

  useEffect(() => {
    if (!driverPos || stops.length < 2) return;
    let cancelled = false;
    setLoading(true);
    optimizeTrip(driverPos, stops)
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        // Compute naive (received-order) total distance for the savings
        // badge. Sum Haversine driver→s1→s2→…→sN.
        let prev = driverPos;
        let naiveKm = 0;
        for (const s of stops) {
          const d = haversineKm(prev, s);
          if (d !== null) naiveKm += d;
          prev = s;
        }
        setNaiveDistance(naiveKm * 1000);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverKey, stopsKey]);

  // ----- Render gate -----
  if (stops.length < 2) return null; // single-stop already has Navigate
  if (!driverPos && !geoError) {
    return (
      <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 mb-4 flex items-center gap-2 text-sm text-emerald-200">
        <Loader2 className="w-4 h-4 animate-spin" /> Locking driver GPS for route optimization…
      </div>
    );
  }
  if (geoError) {
    return (
      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 mb-4 flex items-start gap-2 text-sm text-amber-200">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Allow location for multi-stop routing</div>
          <div className="text-xs opacity-80 mt-0.5">{geoError}</div>
        </div>
      </div>
    );
  }

  const ordered = result?.ordered || stops;
  const truncated = ordered.length > MAX_GMAPS_STOPS;
  const visible = ordered.slice(0, MAX_GMAPS_STOPS);
  const totalKm = result?.distance ? result.distance / 1000 : null;
  const totalMin = result?.duration ? Math.max(1, Math.round(result.duration / 60)) : null;
  const savedMeters =
    naiveDistance !== null && result?.distance !== null && result?.distance !== undefined
      ? Math.max(0, naiveDistance - result.distance)
      : null;
  const showSavings = savedMeters !== null && savedMeters > 300; // <300m noise

  const fmtDist = (m) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);
  const fmtEta = (mins) =>
    mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-400/5 border border-emerald-500/30 p-4 mb-4 shadow-xl shadow-emerald-900/20"
      data-testid="multi-stop-route"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Route className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold tracking-[0.2em] text-emerald-300 uppercase">
            Multi-stop route · {stops.length} stops
          </div>
          <div className="font-display text-base text-white truncate">
            {loading && !result ? (
              <span className="text-emerald-200 inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Optimizing sequence…
              </span>
            ) : totalKm !== null ? (
              <>
                {fmtDist(totalKm * 1000)}
                {totalMin && <span className="text-emerald-300"> · {fmtEta(totalMin)}</span>}
              </>
            ) : (
              <span className="text-slate-300">Sequence ready</span>
            )}
          </div>
        </div>
        {showSavings && (
          <div
            className="rounded-full bg-emerald-500/20 border border-emerald-400/40 px-2.5 py-1 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-200"
            data-testid="multi-stop-savings"
          >
            <TrendingDown className="w-3 h-3" />
            Saves {fmtDist(savedMeters)}
          </div>
        )}
      </div>

      {/* Optimal stop list */}
      <ol className="space-y-1.5 mb-3" data-testid="multi-stop-sequence">
        {visible.map((s, i) => (
          <li key={s.id} className="flex items-center gap-2.5 text-sm">
            <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
              {i + 1}
            </span>
            <span className="text-slate-200 truncate flex-1">{s.label}</span>
            {s.order_number && (
              <span className="text-[10px] font-mono text-slate-500 flex-shrink-0">
                {s.order_number.slice(-6)}
              </span>
            )}
          </li>
        ))}
      </ol>

      {truncated && (
        <div className="text-[11px] text-amber-200 mb-3 leading-snug">
          Google Maps allows max 10 stops per route — only the first {MAX_GMAPS_STOPS}{' '}
          stops will open in turn-by-turn. Run the optimizer again after completing them.
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          // Fire-and-forget: write the audit row before opening Google
          // Maps so the admin dispatch fleet-savings card sees every
          // run. Never block the user — if the POST fails the
          // navigation still happens.
          if (result && visible.length >= 2) {
            axios.post(
              `${API}/driver/route-optimization-log`,
              {
                order_ids: visible.map((s) => s.id),
                stop_count: visible.length,
                optimized_distance_m: result.distance || 0,
                naive_distance_m: naiveDistance || 0,
                source: result.source || 'nearest-neighbor',
              },
              { headers: authHeaders },
            ).catch(() => { /* silent */ });
          }
          openGoogleMapsMultiStop(visible);
        }}
        disabled={!visible.length || loading}
        className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/40 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-3 inline-flex items-center justify-center gap-2 transition active:scale-[0.98]"
        data-testid="multi-stop-navigate-btn"
      >
        <Navigation className="w-4 h-4" />
        Navigate all stops in Google Maps
      </button>

      {result?.source === 'nearest-neighbor' && (
        <div className="mt-2 text-[10px] text-slate-400 text-center">
          Offline sequence (OSRM unavailable) — route may not be globally optimal.
        </div>
      )}
    </div>
  );
};

export default MultiStopRoute;
